import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { noop } from 'lodash-es';
import type { BorderProps, MaxHeightProps, MaxWidthProps, SizeProps } from 'styled-system';
import {
  backgroundColor,
  border,
  borderRadius,
  color,
  maxHeight,
  maxWidth,
  size,
  space,
} from 'styled-system';

import { focusOutlineCSSWithOffset } from '../..//theme/styles';
import { useKeyboardSelect } from '../../hooks/keyboard';
import { useLocalization } from '../../providers/LocaleProvider';
import type { BackgroundColorProps, ColorProps, SpaceProps } from '../../theme/system-types';
import type { CursorProps, OutlineColorProps } from '../../theme/system-utilities';
import { cursor, outlineColor } from '../../theme/system-utilities';
import { ButtonBase } from '../Button/Base';
import Icon from '../Icon';

const Button = styled(ButtonBase)<
  BackgroundColorProps & BorderProps & CursorProps & OutlineColorProps
>`
  ${backgroundColor};
  ${border};
  ${borderRadius};
  ${cursor};
  ${outlineColor};

  ${focusOutlineCSSWithOffset};
`;

const buttonStyles: ComponentProps<typeof Button> = {
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 'circle',
  cursor: 'pointer',
  outlineColor: 'sys.secondary.border.focus',
  mx: ['sp_20', 'sp_32', 'sp_48', 'sp_64'],
  my: 'sp_12',
};

const CLOSE_ICON_SIZE = 32;
const CloseIcon = styled(Icon)<
  SizeProps &
    MaxWidthProps &
    MaxHeightProps &
    ColorProps &
    SpaceProps &
    CursorProps &
    OutlineColorProps
>`
  ${size};
  ${maxWidth};
  ${maxHeight};
  ${color};
  ${space};
  ${cursor};
`;

const closeIconStyles: Partial<ComponentProps<typeof CloseIcon>> = {
  size: CLOSE_ICON_SIZE,
  role: 'presentation',
  color: 'sys.neutral.border.hover',
  maxWidth: CLOSE_ICON_SIZE,
  maxHeight: CLOSE_ICON_SIZE,
  cursor: 'pointer',
};

const CloseButton = ({
  onClick = noop,
  productGalleryV2 = false,
}: {
  onClick?: () => void;
  productGalleryV2?: boolean;
}) => {
  const { t } = useLocalization();
  const handleKeyDown = useKeyboardSelect(onClick);

  return (
    <Button
      {...buttonStyles}
      {...(productGalleryV2 && { mx: 'auto' })}
      data-testid="close-icon"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      aria-label={t('h24_close_image_gallery')}
    >
      <CloseIcon {...closeIconStyles} name="close" />
    </Button>
  );
};

export default CloseButton;
